import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Star, UserPlus } from 'lucide-react';
import { getGPTs, getRegistrations, getCategories } from './services/database';
import { GPT, Category } from './types';
import GPTCard from './components/GPTCard';
import RegistrationModal from './components/RegistrationModal';
import { useAuth } from './contexts/AuthContext';

interface DashboardProps {
  filters: {
    searchTerm: string;
    selectedCategory: string;
    selectedTags: string[];
  };
  gptType: 'general' | 'client';
}

const Dashboard: React.FC<DashboardProps> = ({ filters, gptType }) => {
  const { user } = useAuth();
  const [allGPTs, setAllGPTs] = useState<GPT[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['pinned']));
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [hasRegistered, setHasRegistered] = useState(false);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(true);

  // Check if user has already registered by checking database
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      if (user) {
        try {
          const registrations = await getRegistrations();
          const userRegistration = registrations.find(reg => 
            reg.loginEmail === user.email || reg.email === user.email
          );
          setHasRegistered(!!userRegistration);
        } catch (error) {
          console.error('Error checking registration status:', error);
          setHasRegistered(false);
        } finally {
          setIsCheckingRegistration(false);
        }
      } else {
        setIsCheckingRegistration(false);
      }
    };

    checkRegistrationStatus();
  }, [user]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [gpts, cats] = await Promise.all([getGPTs(), getCategories()]);
        console.log('Dashboard useEffect - GPTs from database:', gpts);
        console.log('Dashboard useEffect - Categories from database:', cats);
        
        // Ensure pinned GPT is always at the top
        const pinnedGPT = gpts.find((gpt: GPT) => gpt.id === 'hunterlodge-general-assistant');
        if (pinnedGPT) {
          const otherGPTs = gpts.filter((gpt: GPT) => gpt.id !== 'hunterlodge-general-assistant');
          const sortedGPTs = [pinnedGPT, ...otherGPTs];
          setAllGPTs(sortedGPTs);
        } else {
          setAllGPTs(gpts);
        }
        
        setCategories(cats);
      } catch (error) {
        console.error('Error loading data:', error);
        setAllGPTs([]);
        setCategories([]);
      }
    };

    loadData();
  }, []);

  // Group GPTs by category
  const groupedGPTs = allGPTs.reduce((acc, gpt) => {
    const category = gpt.category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(gpt);
    return acc;
  }, {} as Record<string, GPT[]>);

  // Get pinned GPTs first
  const pinnedGPTs = allGPTs.filter(gpt => gpt.isPinned);
  
  // Sort categories using database order
  // Show all active categories, even if they don't have GPTs yet
  const sortedCategories = categories
    .filter(cat => cat.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(cat => cat.name);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Helper function to get category display name
  const getCategoryDisplayName = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    return category ? category.displayName : categoryName;
  };

  // Helper function to get category description
  const getCategoryDescription = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    return category ? category.description : '';
  };

  const filteredGPTs = allGPTs.filter(gpt => {
    const matchesSearch = gpt.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                         gpt.description.toLowerCase().includes(filters.searchTerm.toLowerCase());
    
    const matchesCategory = !filters.selectedCategory || gpt.category === filters.selectedCategory;
    
    const matchesTags = filters.selectedTags.length === 0 ||
                                               filters.selectedTags.some((tag: string) => gpt.tags.includes(tag));
    
    const matchesGptType = gpt.gptType === gptType;
    
    return matchesSearch && matchesCategory && matchesTags && matchesGptType;
  });

  const filteredGroupedGPTs = filteredGPTs.reduce((acc, gpt) => {
    const category = gpt.category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(gpt);
    return acc;
  }, {} as Record<string, GPT[]>);

  // Show all active categories, even if they don't have GPTs matching the current filters
  const filteredSortedCategories = categories
    .filter(cat => cat.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(cat => cat.name);

  const handleRegistrationSuccess = () => {
    setHasRegistered(true);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  if (filteredGPTs.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400 text-lg">No GPTs found matching your criteria.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Registration Section - Removed as redundant - invite links are now in GPT cards */}

      {/* Pinned GPTs Section */}
      {pinnedGPTs.length > 0 && (
        <div className="border border-yellow-400 shadow-lg bg-gradient-to-r from-yellow-900/20 to-[#21282D] rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-yellow-400">
            <div className="flex items-center space-x-3">
              <Star className="w-5 h-5 text-yellow-500 fill-current" />
              <div>
                <h3 className="font-semibold text-yellow-300">🌟 Pinned GPTs</h3>
                <p className="text-sm text-yellow-400">Essential tools always available at the top</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pinnedGPTs.map((gpt: GPT) => (
                <GPTCard key={gpt.id} gpt={gpt} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Regular Categories */}
      {filteredSortedCategories.map(category => {
        const gpts = filteredGroupedGPTs[category] || [];
        const isExpanded = expandedCategories.has(category);

        return (
          <div
            key={category}
            className="border border-gray-600 bg-[#21282D] rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggleCategory(category)}
              className="w-full px-6 py-4 text-left flex items-center justify-between transition-colors duration-200 hover:bg-gray-700"
            >
              <div className="flex items-center space-x-3">
                <div>
                  <h3 className="font-semibold text-gray-100">
                    {getCategoryDisplayName(category)}
                  </h3>
                  <p className="text-sm text-gray-300">
                    {getCategoryDescription(category) || (gpts.length > 0 ? `${gpts.length} GPT${gpts.length !== 1 ? 's' : ''} available` : 'No GPTs in this category yet')}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400">
                  {gpts.length} GPT{gpts.length !== 1 ? 's' : ''}
                </span>
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </button>
            
            {isExpanded && (
              <div className="border-t border-gray-600 bg-[#1B2225] p-6">
                {gpts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {gpts.map((gpt: GPT) => (
                      <GPTCard key={gpt.id} gpt={gpt} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No GPTs in this category yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Dashboard; 